/**
 * Hurricane Prep Mode — E5.
 * Storm tracker mini-map, 10-item prep checklist, emergency contacts,
 * and P3-hotel generator coordination card.
 * Atlas: E5.
 */
"use client";

import dynamic from "next/dynamic";
import Link from "next/link";
import { ChevronLeft, ExternalLink } from "lucide-react";
import { useActivePersona } from "@/lib/persona";
import { useHurricaneAnchor, useHurricaneSeason, isHotelPersona, formatStormEta } from "@petci/shared-ui";
import { getDemoStorm } from "@petci/fixtures";
import { useT, useLocaleTag } from "@/lib/locale";
import { PrepChecklist } from "@/components/PrepChecklist";
import { EmergencyContacts } from "@/components/EmergencyContacts";
import { GeneratorCoordinationCard } from "@/components/GeneratorCoordinationCard";

/** Keyed fallback while the storm-map chunk downloads (D1). */
function StormMapLoading() {
  const t = useT();
  return (
    <div className="w-full h-48 bg-neutral-100 rounded-xl flex items-center justify-center">
      <span className="text-sm text-neutral-500">{t("hurricane.loadingStormMap")}</span>
    </div>
  );
}

const StormTrackerMap = dynamic(
  () => import("@/components/StormTrackerMap").then((m) => m.StormTrackerMap),
  {
    ssr: false,
    loading: () => <StormMapLoading />,
  },
);

export default function HurricanePage() {
  const t = useT();
  const tag = useLocaleTag();
  const persona = useActivePersona();
  const now = useHurricaneAnchor(process.env.NEXT_PUBLIC_HURRICANE_ANCHOR_NOW);
  const inSeason = useHurricaneSeason(process.env.NEXT_PUBLIC_FORCE_SEASON);
  const storm = getDemoStorm(now);

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {/* Back */}
      <Link
        href="/home"
        className="flex items-center gap-1 text-sm text-neutral-700 hover:text-neutral-900 transition-colors duration-fast py-1 -my-1"
      >
        <ChevronLeft size={16} aria-hidden />
        {t("nav.home")}
      </Link>

      {/* Header */}
      <div className="space-y-1">
        <div className="flex items-center gap-2">
          <h1 className="text-2xl font-display font-bold text-neutral-900">
            {t("hurricane.prepModeTitle")}
          </h1>
          {inSeason && (
            <span className="badge bg-semantic-warning-strong text-white font-semibold">
              {t("hurricane.seasonActiveBadge")}
            </span>
          )}
        </div>
        <p className="text-sm text-neutral-700">
          {inSeason
            ? t("hurricane.stormBannerMessage", {
                name: storm.name,
                eta: formatStormEta(storm.etaIso, tag),
              })
            : t("hurricane.offSeasonMessage")}
        </p>
      </div>

      {/* Storm tracker map */}
      {inSeason && <StormTrackerMap />}

      {/* Prep checklist */}
      <PrepChecklist personaId={persona.id} />

      {/* P3 hotel generator coordination */}
      {isHotelPersona(persona.id) && <GeneratorCoordinationCard />}

      {/* Emergency contacts */}
      <EmergencyContacts />

      {/* Government shelter link */}
      <div className="card-surface flex items-center justify-between gap-3">
        <div className="space-y-0.5">
          <p className="text-sm font-semibold text-neutral-800">
            {t("hurricane.shelterRegister")}
          </p>
          <p className="text-xs text-neutral-700">
            {t("hurricane.shelterDept")}
          </p>
        </div>
        <a
          href="https://www.gov.tc/dcr/"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1.5 text-sm text-brand-primary-text font-semibold hover:underline flex-shrink-0 py-1 -my-1"
        >
          gov.tc/dcr
          <ExternalLink size={13} aria-hidden />
        </a>
      </div>
    </div>
  );
}
